/** Persistent, bounded diagnostics that survive wallet redirects. */
import { redactPii } from './redact'

const KEY = '__spotty_diag_trace'
const MAX_ENTRIES = 200
const MAX_AGE_MS = 24 * 60 * 60 * 1000
const SAFE_RESUME_ENTRIES = new Set([
  'header',
  'hero',
  'chart',
  'footer',
  'extra_spins',
  'community',
])

export interface DiagEntry {
  ts: number
  label: string
  data?: Record<string, unknown>
}

export function readDiagTrace(): DiagEntry[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]')
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (entry): entry is DiagEntry =>
          typeof entry?.ts === 'number' &&
          entry.ts > Date.now() - MAX_AGE_MS &&
          typeof entry.label === 'string'
      )
      .slice(-MAX_ENTRIES)
  } catch {
    return []
  }
}

/** Remove only the acknowledged snapshot; preserve entries added during the request. */
export function acknowledgeDiagTrace(sent: DiagEntry[]): void {
  try {
    const remaining = readDiagTrace()
    for (const entry of sent) {
      const serialized = JSON.stringify(entry)
      const index = remaining.findIndex((candidate) => JSON.stringify(candidate) === serialized)
      if (index >= 0) remaining.splice(index, 1)
    }
    localStorage.setItem(KEY, JSON.stringify(remaining))
  } catch {
    // A retained event may be delivered twice; losing new entries is worse.
  }
}

/** Strip wallet callback material while retaining the harmless resume marker. */
export function safeDiagnosticUrl(raw: string): string {
  try {
    const source = new URL(raw)
    const safe = new URL(source.pathname, source.origin)
    if (source.searchParams.get('phantom_resume') === '1') {
      safe.searchParams.set('phantom_resume', '1')
      const entry = source.searchParams.get('entry')
      if (entry && SAFE_RESUME_ENTRIES.has(entry)) safe.searchParams.set('entry', entry)
    }
    return safe.toString()
  } catch {
    return ''
  }
}

export function diagLog(label: string, data?: Record<string, unknown>): void {
  try {
    const safeData = redactPii(data) as Record<string, unknown> | undefined
    // eslint-disable-next-line no-console
    console.log(`[SPOTTY-DIAG] ${label}`, safeData ?? '')
    if (typeof localStorage === 'undefined') return
    const existing = readDiagTrace()
    const entry = { ts: Date.now(), label: label.slice(0, 128), data: safeData }
    // Match the backend per-entry ceiling, leaving room for transport context.
    if (new TextEncoder().encode(JSON.stringify(entry)).length > 6000) {
      entry.data = { truncated: true }
    }
    existing.push(entry)
    // Cap growth so localStorage doesn't overflow across long sessions.
    const trimmed = existing.length > MAX_ENTRIES ? existing.slice(-MAX_ENTRIES) : existing
    localStorage.setItem(KEY, JSON.stringify(trimmed))
  } catch {
    /* non-fatal */
  }
}

export function clearDiagTrace(): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.removeItem(KEY)
  } catch {
    /* non-fatal */
  }
}
